import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Truck,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
  Calendar,
  RefreshCw,
  Plus,
  Eye,
  User,
  DollarSign,
  Filter,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  Check,
  Edit,
  Trash2,
  UserMinus,
  ArrowRightLeft,
  ShoppingBag,
} from "lucide-react";

const ZONAS = [
  { value: "norte", label: "Norte" },
  { value: "nororiente", label: "Nororiente" },
  { value: "noroccidente", label: "Noroccidente" },
  { value: "centro", label: "Centro" },
  { value: "chapinero", label: "Chapinero" },
  { value: "occidente", label: "Occidente" },
  { value: "sur_occidente", label: "Sur Occidente" },
  { value: "sur", label: "Sur" },
  { value: "soacha", label: "Soacha" },
  { value: "especial", label: "Especial" },
];

const FRANJAS = [
  { value: "manana", label: "Mañana (8AM-12PM)" },
  { value: "tarde", label: "Tarde (12PM-5PM)" },
  { value: "noche", label: "Noche (5PM-8PM)" },
  { value: "personalizada", label: "Personalizada" },
];

const DELIVERY_STATUS_CONFIG = {
  pending_dispatch: { label: "Pendiente", color: "bg-gray-500", icon: Clock },
  assigned: { label: "Asignado", color: "bg-blue-500", icon: User },
  in_preparation: { label: "En Alistamiento", color: "bg-yellow-500", icon: Package },
  ready_for_dispatch: { label: "Listo", color: "bg-purple-500", icon: CheckCircle },
  picked_up: { label: "Recogido", color: "bg-indigo-500", icon: Truck },
  on_route: { label: "En Ruta", color: "bg-blue-600", icon: Truck },
  delivered: { label: "Entregado", color: "bg-green-600", icon: CheckCircle },
  failed_delivery: { label: "No Entregado", color: "bg-red-500", icon: XCircle },
  rescheduled: { label: "Reprogramado", color: "bg-orange-500", icon: Calendar },
  cancelled_dispatch: { label: "Cancelado", color: "bg-gray-600", icon: XCircle },
};

const DISPATCH_STATUS_CONFIG = {
  draft: { label: "Borrador", color: "bg-gray-400" },
  assigned: { label: "Asignada", color: "bg-blue-500" },
  in_progress: { label: "En Progreso", color: "bg-yellow-500" },
  completed: { label: "Completada", color: "bg-green-600" },
  partially_completed: { label: "Parcial", color: "bg-orange-500" },
  cancelled: { label: "Cancelada", color: "bg-red-500" },
};

// Get Colombia date (UTC-5)
const getColombiaDate = () => {
  const now = new Date();
  // Colombia is UTC-5
  const colombiaOffset = -5 * 60; // minutes
  const localOffset = now.getTimezoneOffset(); // minutes
  const diff = colombiaOffset - localOffset;
  const colombiaTime = new Date(now.getTime() + diff * 60 * 1000);
  return colombiaTime.toISOString().split("T")[0];
};

export default function AdminDespachos() {
  const [activeTab, setActiveTab] = useState("rutas"); // rutas, pendientes, stats
  const [rutas, setRutas] = useState([]);
  const [pedidosPendientes, setPedidosPendientes] = useState([]);
  const [domiciliarios, setDomiciliarios] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getColombiaDate());
  
  // Selection for creating route
  const [selectedPedidos, setSelectedPedidos] = useState(new Set());
  const [showCreateRouteDialog, setShowCreateRouteDialog] = useState(false);
  const [routeFormData, setRouteFormData] = useState({
    name: "",
    dispatch_date: getColombiaDate(),
    time_window: "manana",
    zone: "",
    assigned_to_user_id: "",
    assigned_to_name: "",
    assigned_to_role: "",
    notes: "",
  });
  
  // Route detail
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  
  // Edit route dialog
  const [showEditRouteDialog, setShowEditRouteDialog] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  
  // Reassign order dialog
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignOrderId, setReassignOrderId] = useState(null);
  const [reassignTargetRouteId, setReassignTargetRouteId] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rutasRes, domiciliariosRes, statsRes] = await Promise.all([
        api.get(`/despachos/rutas?fecha=${selectedDate}`),
        api.get("/despachos/domiciliarios"),
        api.get(`/despachos/stats?fecha=${selectedDate}`),
      ]);
      
      setRutas(rutasRes.data || []);
      setDomiciliarios([
        ...(domiciliariosRes.data.domiciliarios || []),
        ...(domiciliariosRes.data.vendedores_entrega || []),
      ]);
      setStats(statsRes.data);
      
      // Fetch pending orders (without dispatch assigned)
      const [catalogRes, tercerosRes] = await Promise.all([
        api.get("/catalogo/admin/orders?estado=confirmado"),
        api.get("/pedidos-terceros?estado=confirmado"),
      ]);
      
      // Filter orders without dispatch
      const catalogPending = (catalogRes.data.pedidos || [])
        .filter(p => !p.assigned_dispatch_id && p.estado !== "entregado" && p.estado !== "cancelado")
        .map(p => ({ ...p, origen: "catalogo" }));
      
      const tercerosPending = (tercerosRes.data || [])
        .filter(p => !p.assigned_dispatch_id && p.estado !== "entregado" && p.estado !== "cancelado")
        .map(p => ({ ...p, origen: "tercero" }));
      
      setPedidosPendientes([...catalogPending, ...tercerosPending]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateRoute = async () => {
    if (!routeFormData.name || !routeFormData.assigned_to_user_id || selectedPedidos.size === 0) {
      toast.error("Completa todos los campos y selecciona al menos un pedido");
      return;
    }

    try {
      const orderIds = Array.from(selectedPedidos);
      await api.post("/despachos/rutas", {
        ...routeFormData,
        order_ids: orderIds,
      });
      
      toast.success("Ruta creada exitosamente");
      setShowCreateRouteDialog(false);
      setSelectedPedidos(new Set());
      setRouteFormData({
        name: "",
        dispatch_date: new Date().toISOString().split("T")[0],
        time_window: "manana",
        zone: "",
        assigned_to_user_id: "",
        assigned_to_name: "",
        assigned_to_role: "",
        notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear ruta");
    }
  };

  const handleSelectDomiciliario = (domiciliario) => {
    setRouteFormData({
      ...routeFormData,
      assigned_to_user_id: domiciliario.id,
      assigned_to_name: domiciliario.nombre,
      assigned_to_role: domiciliario.tipo_entregador || domiciliario.rol,
    });
  };

  const togglePedidoSelection = (pedidoId) => {
    const newSelection = new Set(selectedPedidos);
    if (newSelection.has(pedidoId)) {
      newSelection.delete(pedidoId);
    } else {
      newSelection.add(pedidoId);
    }
    setSelectedPedidos(newSelection);
  };

  const selectAllPedidos = () => {
    if (selectedPedidos.size === pedidosPendientes.length) {
      setSelectedPedidos(new Set());
    } else {
      setSelectedPedidos(new Set(pedidosPendientes.map(p => p.id)));
    }
  };

  const toggleOrderExpanded = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleOpenEditRoute = (route) => {
    setEditingRoute({
      ...route,
      assigned_to_user_id: route.assigned_to_user_id || "",
      assigned_to_name: route.assigned_to_name || "",
    });
    setShowEditRouteDialog(true);
  };

  const handleUpdateRoute = async () => {
    if (!editingRoute) return;
    
    try {
      await api.put(`/despachos/rutas/${editingRoute.id}`, {
        name: editingRoute.name,
        time_window: editingRoute.time_window,
        zone: editingRoute.zone,
        assigned_to_user_id: editingRoute.assigned_to_user_id,
        assigned_to_name: editingRoute.assigned_to_name,
        notes: editingRoute.notes,
      });
      
      toast.success("Ruta actualizada");
      setShowEditRouteDialog(false);
      setEditingRoute(null);
      setSelectedRoute(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar ruta");
    }
  };

  const handleUnassignOrder = async (routeId, orderId) => {
    if (!window.confirm("¿Desasignar este pedido de la ruta? El pedido volverá a la lista de pendientes.")) {
      return;
    }
    
    try {
      await api.post(`/despachos/rutas/${routeId}/unassign-order`, {
        order_id: orderId,
      });
      
      toast.success("Pedido desasignado de la ruta");
      // Refresh route data
      const updatedRoute = await api.get(`/despachos/rutas/${routeId}`);
      setSelectedRoute(updatedRoute.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desasignar pedido");
    }
  };

  const handleOpenReassign = (orderId) => {
    setReassignOrderId(orderId);
    setReassignTargetRouteId("");
    setShowReassignDialog(true);
  };

  const handleReassignOrder = async () => {
    if (!reassignOrderId || !reassignTargetRouteId) {
      toast.error("Selecciona una ruta destino");
      return;
    }
    
    try {
      await api.post(`/despachos/rutas/${reassignTargetRouteId}/assign-order`, {
        order_id: reassignOrderId,
        source_route_id: selectedRoute?.id,
      });
      
      toast.success("Pedido reasignado exitosamente");
      setShowReassignDialog(false);
      setReassignOrderId(null);
      
      // Refresh route data
      if (selectedRoute) {
        const updatedRoute = await api.get(`/despachos/rutas/${selectedRoute.id}`);
        setSelectedRoute(updatedRoute.data);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reasignar pedido");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C8902A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-despachos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
            Despachos
          </h1>
          <p className="text-[#4A2C0A]/60 font-['Poppins'] text-sm">
            Gestiona rutas y entregas
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40 border-[#E5D4B3]"
          />
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2 border-[#E5D4B3]"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
            <p className="text-xs text-[#4A2C0A]/60">Rutas</p>
            <p className="text-2xl font-bold text-[#4A2C0A]">{stats.total_rutas}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200">
            <p className="text-xs text-blue-600">Pedidos</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_orders}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-200">
            <p className="text-xs text-green-600">Entregados</p>
            <p className="text-2xl font-bold text-green-600">{stats.total_delivered}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-yellow-200">
            <p className="text-xs text-yellow-600">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.total_pending}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-red-200">
            <p className="text-xs text-red-600">Fallidos</p>
            <p className="text-2xl font-bold text-red-600">{stats.total_failed}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#C8902A]/30">
            <p className="text-xs text-[#C8902A]">Cumplimiento</p>
            <p className="text-2xl font-bold text-[#C8902A]">{stats.completion_rate}%</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E5D4B3]">
        {[
          { id: "rutas", label: "Rutas Activas", count: rutas.length },
          { id: "pendientes", label: "Pedidos por Despachar", count: pedidosPendientes.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-[#C8902A] text-[#C8902A]"
                : "border-transparent text-[#4A2C0A]/60 hover:text-[#4A2C0A]"
            }`}
          >
            {tab.label}
            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
              activeTab === tab.id ? "bg-[#C8902A]/10" : "bg-gray-100"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content: Rutas Activas */}
      {activeTab === "rutas" && (
        <div className="space-y-4">
          {rutas.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-[#E5D4B3]">
              <Truck className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
              <p className="text-[#4A2C0A]">No hay rutas para esta fecha</p>
              <p className="text-sm text-[#4A2C0A]/60 mt-1">
                Crea una ruta desde la pestaña &quot;Pedidos por Despachar&quot;
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rutas.map((ruta) => {
                const statusConfig = DISPATCH_STATUS_CONFIG[ruta.status] || DISPATCH_STATUS_CONFIG.draft;
                return (
                  <div
                    key={ruta.id}
                    className="bg-white rounded-xl border-2 border-[#E5D4B3] hover:border-[#C8902A]/50 transition-all cursor-pointer"
                    onClick={() => setSelectedRoute(ruta)}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-[#4A2C0A]">{ruta.name}</h3>
                          <p className="text-sm text-[#4A2C0A]/60">{ruta.assigned_to_name}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs text-white rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center mb-3">
                        <div className="bg-[#FBF5E8] rounded-lg p-2">
                          <p className="text-lg font-bold text-[#4A2C0A]">{ruta.total_orders}</p>
                          <p className="text-[10px] text-[#4A2C0A]/60">Total</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-green-600">{ruta.orders_delivered}</p>
                          <p className="text-[10px] text-green-600">Entregados</p>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-yellow-600">{ruta.orders_pending}</p>
                          <p className="text-[10px] text-yellow-600">Pendientes</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-[#4A2C0A]/60">
                          <Clock className="w-3 h-3" />
                          <span>{FRANJAS.find(f => f.value === ruta.time_window)?.label || ruta.time_window}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#C8902A]">
                          <DollarSign className="w-3 h-3" />
                          <span>${ruta.total_cash_to_collect?.toLocaleString("es-CO")}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="px-4 py-2 bg-[#FBF5E8]/50 border-t border-[#E5D4B3] flex items-center justify-between">
                      <span className="text-xs text-[#4A2C0A]/60">
                        {ruta.zone ? ZONAS.find(z => z.value === ruta.zone)?.label : "Sin zona"}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#4A2C0A]/40" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Pedidos por Despachar */}
      {activeTab === "pendientes" && (
        <div className="space-y-4">
          {/* Action Bar */}
          {selectedPedidos.size > 0 && (
            <div className="bg-[#C8902A]/10 border border-[#C8902A]/30 rounded-xl p-4 flex items-center justify-between">
              <span className="text-sm text-[#4A2C0A]">
                <strong>{selectedPedidos.size}</strong> pedidos seleccionados
              </span>
              <Button
                onClick={() => setShowCreateRouteDialog(true)}
                className="gap-2 bg-[#C8902A] hover:bg-[#A67720]"
              >
                <Plus className="w-4 h-4" />
                Crear Ruta
              </Button>
            </div>
          )}

          {/* Table Header */}
          <div className="bg-white rounded-xl border border-[#E5D4B3] overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#FBF5E8] border-b border-[#E5D4B3] text-xs font-semibold text-[#4A2C0A]/70">
              <div className="col-span-1 flex items-center">
                <Checkbox
                  checked={selectedPedidos.size === pedidosPendientes.length && pedidosPendientes.length > 0}
                  onCheckedChange={selectAllPedidos}
                />
              </div>
              <div className="col-span-2">Pedido</div>
              <div className="col-span-2">Cliente</div>
              <div className="col-span-2">Dirección</div>
              <div className="col-span-1">Barrio</div>
              <div className="col-span-1">Fecha</div>
              <div className="col-span-1">Total</div>
              <div className="col-span-1">Pago</div>
              <div className="col-span-1">Estado</div>
            </div>

            {pedidosPendientes.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p className="text-[#4A2C0A]">No hay pedidos pendientes</p>
                <p className="text-sm text-[#4A2C0A]/60">Todos los pedidos han sido asignados a rutas</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E5D4B3]">
                {pedidosPendientes.map((pedido) => {
                  const deliveryStatus = DELIVERY_STATUS_CONFIG[pedido.delivery_status] || DELIVERY_STATUS_CONFIG.pending_dispatch;
                  return (
                    <div
                      key={pedido.id}
                      className={`grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-[#FBF5E8]/50 transition-colors ${
                        selectedPedidos.has(pedido.id) ? "bg-[#C8902A]/5" : ""
                      }`}
                    >
                      <div className="col-span-1 flex items-center">
                        <Checkbox
                          checked={selectedPedidos.has(pedido.id)}
                          onCheckedChange={() => togglePedidoSelection(pedido.id)}
                        />
                      </div>
                      <div className="col-span-2">
                        <p className="font-medium text-[#4A2C0A]">{pedido.numero_pedido}</p>
                        <p className="text-xs text-[#4A2C0A]/50 capitalize">{pedido.origen}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[#4A2C0A] truncate">{pedido.cliente_nombre}</p>
                        <a
                          href={`https://wa.me/57${pedido.cliente_telefono?.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-600 hover:underline flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="w-3 h-3" />
                          {pedido.cliente_telefono}
                        </a>
                      </div>
                      <div className="col-span-2 text-[#4A2C0A]/70 truncate">
                        {pedido.direccion_entrega || pedido.cliente_direccion}
                      </div>
                      <div className="col-span-1 text-[#4A2C0A]/70 truncate">
                        {pedido.barrio_entrega || pedido.barrio || "-"}
                      </div>
                      <div className="col-span-1 text-[#4A2C0A]/70">
                        {pedido.fecha_entrega?.split("T")[0] || "-"}
                      </div>
                      <div className="col-span-1 font-medium text-[#4A2C0A]">
                        ${pedido.total?.toLocaleString("es-CO")}
                      </div>
                      <div className="col-span-1 text-xs">
                        <span className={`px-2 py-1 rounded-full ${
                          pedido.metodo_pago?.includes("contra") || pedido.metodo_pago?.includes("efectivo")
                            ? "bg-orange-100 text-orange-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {pedido.metodo_pago?.replace(/_/g, " ").slice(0, 10) || "N/A"}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs text-white rounded-full ${deliveryStatus.color}`}>
                          {deliveryStatus.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Route Dialog */}
      <Dialog open={showCreateRouteDialog} onOpenChange={setShowCreateRouteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">Crear Nueva Ruta</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#4A2C0A]">Nombre de la Ruta *</Label>
              <Input
                value={routeFormData.name}
                onChange={(e) => setRouteFormData({ ...routeFormData, name: e.target.value })}
                placeholder="Ej: Ruta Norte Mañana"
                className="border-[#E5D4B3]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#4A2C0A]">Fecha de Despacho</Label>
                <Input
                  type="date"
                  value={routeFormData.dispatch_date}
                  onChange={(e) => setRouteFormData({ ...routeFormData, dispatch_date: e.target.value })}
                  className="border-[#E5D4B3]"
                />
              </div>
              <div>
                <Label className="text-[#4A2C0A]">Franja Horaria</Label>
                <Select
                  value={routeFormData.time_window}
                  onValueChange={(value) => setRouteFormData({ ...routeFormData, time_window: value })}
                >
                  <SelectTrigger className="border-[#E5D4B3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRANJAS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Zona</Label>
              <Select
                value={routeFormData.zone}
                onValueChange={(value) => setRouteFormData({ ...routeFormData, zone: value })}
              >
                <SelectTrigger className="border-[#E5D4B3]">
                  <SelectValue placeholder="Seleccionar zona..." />
                </SelectTrigger>
                <SelectContent>
                  {ZONAS.map((z) => (
                    <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Asignar a *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                {domiciliarios.map((dom) => (
                  <button
                    key={dom.id}
                    type="button"
                    onClick={() => handleSelectDomiciliario(dom)}
                    className={`p-3 text-left rounded-lg border-2 transition-colors ${
                      routeFormData.assigned_to_user_id === dom.id
                        ? "border-[#C8902A] bg-[#C8902A]/10"
                        : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                    }`}
                  >
                    <p className="font-medium text-sm text-[#4A2C0A]">{dom.nombre}</p>
                    <p className="text-xs text-[#4A2C0A]/60 capitalize">
                      {dom.tipo_entregador || dom.rol}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Notas</Label>
              <Textarea
                value={routeFormData.notes}
                onChange={(e) => setRouteFormData({ ...routeFormData, notes: e.target.value })}
                placeholder="Observaciones internas..."
                className="border-[#E5D4B3]"
                rows={2}
              />
            </div>

            <div className="bg-[#FBF5E8] rounded-lg p-3">
              <p className="text-sm text-[#4A2C0A]">
                <strong>{selectedPedidos.size}</strong> pedidos serán asignados a esta ruta
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRouteDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateRoute}
              className="bg-[#C8902A] hover:bg-[#A67720]"
            >
              Crear Ruta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Detail Dialog */}
      <Dialog open={!!selectedRoute} onOpenChange={(open) => !open && setSelectedRoute(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedRoute && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#4A2C0A] flex items-center justify-between">
                  <span>{selectedRoute.name}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEditRoute(selectedRoute)}
                      className="gap-1 border-[#E5D4B3]"
                      data-testid="edit-route-btn"
                    >
                      <Edit className="w-4 h-4" />
                      Editar
                    </Button>
                    <span className={`px-3 py-1 text-sm text-white rounded-full ${
                      DISPATCH_STATUS_CONFIG[selectedRoute.status]?.color || "bg-gray-500"
                    }`}>
                      {DISPATCH_STATUS_CONFIG[selectedRoute.status]?.label || selectedRoute.status}
                    </span>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Route Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-[#FBF5E8] rounded-lg p-3">
                    <p className="text-xs text-[#4A2C0A]/60">Domiciliario</p>
                    <p className="font-medium text-[#4A2C0A]">{selectedRoute.assigned_to_name}</p>
                  </div>
                  <div className="bg-[#FBF5E8] rounded-lg p-3">
                    <p className="text-xs text-[#4A2C0A]/60">Franja</p>
                    <p className="font-medium text-[#4A2C0A]">
                      {FRANJAS.find(f => f.value === selectedRoute.time_window)?.label || selectedRoute.time_window}
                    </p>
                  </div>
                  <div className="bg-[#FBF5E8] rounded-lg p-3">
                    <p className="text-xs text-[#4A2C0A]/60">Recaudar</p>
                    <p className="font-medium text-[#C8902A]">
                      ${selectedRoute.total_cash_to_collect?.toLocaleString("es-CO")}
                    </p>
                  </div>
                  <div className="bg-[#FBF5E8] rounded-lg p-3">
                    <p className="text-xs text-[#4A2C0A]/60">Total Valor</p>
                    <p className="font-medium text-[#4A2C0A]">
                      ${selectedRoute.total_value?.toLocaleString("es-CO")}
                    </p>
                  </div>
                </div>

                {/* Orders List with Products */}
                <div>
                  <h3 className="font-semibold text-[#4A2C0A] mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Pedidos ({selectedRoute.orders?.length || 0})
                  </h3>
                  <div className="space-y-2">
                    {selectedRoute.orders?.map((order, idx) => {
                      const status = DELIVERY_STATUS_CONFIG[order.delivery_status] || DELIVERY_STATUS_CONFIG.pending_dispatch;
                      const isExpanded = expandedOrders.has(order.order_id);
                      const canModify = !["delivered", "failed_delivery"].includes(order.delivery_status);
                      
                      return (
                        <div
                          key={order.order_id}
                          className="bg-white border border-[#E5D4B3] rounded-lg overflow-hidden"
                        >
                          {/* Order Header */}
                          <div 
                            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FBF5E8]/50"
                            onClick={() => toggleOrderExpanded(order.order_id)}
                          >
                            <span className="w-6 h-6 bg-[#C8902A]/10 rounded-full flex items-center justify-center text-xs font-bold text-[#C8902A]">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#4A2C0A]">{order.order_number}</p>
                              <p className="text-sm text-[#4A2C0A]/60 truncate">
                                {order.cliente_nombre} • {order.direccion_entrega}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs text-white rounded-full ${status.color}`}>
                              {status.label}
                            </span>
                            <span className="font-medium text-[#4A2C0A]">
                              ${order.total?.toLocaleString("es-CO")}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-[#4A2C0A]/40" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[#4A2C0A]/40" />
                            )}
                          </div>
                          
                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="border-t border-[#E5D4B3] bg-[#FBF5E8]/30">
                              {/* Contact Info */}
                              <div className="p-3 border-b border-[#E5D4B3]/50">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                  <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-green-600" />
                                    <a
                                      href={`https://wa.me/57${order.cliente_telefono?.replace(/\D/g, "")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-green-600 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {order.cliente_telefono}
                                    </a>
                                  </div>
                                  <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                                    <MapPin className="w-4 h-4" />
                                    <span>{order.barrio || order.barrio_entrega || "Sin barrio"}</span>
                                  </div>
                                </div>
                                <p className="mt-2 text-sm text-[#4A2C0A]">{order.direccion_entrega}</p>
                                {order.notas_entrega && (
                                  <p className="mt-1 text-xs text-[#4A2C0A]/60 italic">Notas: {order.notas_entrega}</p>
                                )}
                              </div>
                              
                              {/* Products */}
                              <div className="p-3">
                                <p className="text-xs font-semibold text-[#4A2C0A]/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <ShoppingBag className="w-3 h-3" />
                                  Productos
                                </p>
                                <div className="space-y-1">
                                  {order.items?.map((item, itemIdx) => (
                                    <div
                                      key={itemIdx}
                                      className="flex items-center justify-between py-1.5 px-2 bg-white rounded border border-[#E5D4B3]/50"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="w-5 h-5 bg-[#C8902A]/10 rounded text-[10px] font-bold text-[#C8902A] flex items-center justify-center">
                                          {item.cantidad}
                                        </span>
                                        <span className="text-sm text-[#4A2C0A]">{item.producto_nombre}</span>
                                      </div>
                                      <span className="text-sm font-medium text-[#4A2C0A]">
                                        ${item.subtotal?.toLocaleString("es-CO")}
                                      </span>
                                    </div>
                                  ))}
                                  {(!order.items || order.items.length === 0) && (
                                    <p className="text-xs text-[#4A2C0A]/50 italic py-2">Sin detalle de productos</p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Actions */}
                              {canModify && (
                                <div className="p-3 border-t border-[#E5D4B3]/50 flex gap-2 justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenReassign(order.order_id);
                                    }}
                                    className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                    data-testid={`reassign-order-${order.order_id}`}
                                  >
                                    <ArrowRightLeft className="w-3 h-3" />
                                    Reasignar
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUnassignOrder(selectedRoute.id, order.order_id);
                                    }}
                                    className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                                    data-testid={`unassign-order-${order.order_id}`}
                                  >
                                    <UserMinus className="w-3 h-3" />
                                    Desasignar
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedRoute(null)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Route Dialog */}
      <Dialog open={showEditRouteDialog} onOpenChange={setShowEditRouteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">Editar Ruta</DialogTitle>
          </DialogHeader>

          {editingRoute && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#4A2C0A]">Nombre de la Ruta</Label>
                <Input
                  value={editingRoute.name}
                  onChange={(e) => setEditingRoute({ ...editingRoute, name: e.target.value })}
                  className="border-[#E5D4B3]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#4A2C0A]">Franja Horaria</Label>
                  <Select
                    value={editingRoute.time_window}
                    onValueChange={(value) => setEditingRoute({ ...editingRoute, time_window: value })}
                  >
                    <SelectTrigger className="border-[#E5D4B3]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FRANJAS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#4A2C0A]">Zona</Label>
                  <Select
                    value={editingRoute.zone || ""}
                    onValueChange={(value) => setEditingRoute({ ...editingRoute, zone: value })}
                  >
                    <SelectTrigger className="border-[#E5D4B3]">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ZONAS.map((z) => (
                        <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[#4A2C0A]">Cambiar Domiciliario</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {domiciliarios.map((dom) => (
                    <button
                      key={dom.id}
                      type="button"
                      onClick={() => setEditingRoute({
                        ...editingRoute,
                        assigned_to_user_id: dom.id,
                        assigned_to_name: dom.nombre,
                      })}
                      className={`p-3 text-left rounded-lg border-2 transition-colors ${
                        editingRoute.assigned_to_user_id === dom.id
                          ? "border-[#C8902A] bg-[#C8902A]/10"
                          : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                      }`}
                    >
                      <p className="font-medium text-sm text-[#4A2C0A]">{dom.nombre}</p>
                      <p className="text-xs text-[#4A2C0A]/60 capitalize">
                        {dom.tipo_entregador || dom.rol}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[#4A2C0A]">Notas</Label>
                <Textarea
                  value={editingRoute.notes || ""}
                  onChange={(e) => setEditingRoute({ ...editingRoute, notes: e.target.value })}
                  className="border-[#E5D4B3]"
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRouteDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateRoute}
              className="bg-[#C8902A] hover:bg-[#A67720]"
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Order Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">Reasignar Pedido a Otra Ruta</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-[#4A2C0A]/70">
              Selecciona la ruta destino para mover este pedido:
            </p>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rutas.filter(r => r.id !== selectedRoute?.id && !["completed", "cancelled"].includes(r.status)).map((ruta) => (
                <button
                  key={ruta.id}
                  type="button"
                  onClick={() => setReassignTargetRouteId(ruta.id)}
                  className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                    reassignTargetRouteId === ruta.id
                      ? "border-[#C8902A] bg-[#C8902A]/10"
                      : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-[#4A2C0A]">{ruta.name}</p>
                      <p className="text-xs text-[#4A2C0A]/60">{ruta.assigned_to_name}</p>
                    </div>
                    <span className="text-xs bg-[#FBF5E8] px-2 py-1 rounded text-[#4A2C0A]">
                      {ruta.total_orders} pedidos
                    </span>
                  </div>
                </button>
              ))}
              {rutas.filter(r => r.id !== selectedRoute?.id && !["completed", "cancelled"].includes(r.status)).length === 0 && (
                <p className="text-sm text-[#4A2C0A]/50 text-center py-4">
                  No hay otras rutas disponibles
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleReassignOrder}
              disabled={!reassignTargetRouteId}
              className="bg-[#C8902A] hover:bg-[#A67720]"
            >
              Reasignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
